import { produce } from 'immer';
import { concat } from 'lodash';
import { Reducer } from 'redux';

import {
  ActivityController,
  ActivityDetailsData,
  AppliedActivityListFilters,
  JobDetailsData,
} from '@/controllers/Activity';
import { Opportunity } from '@/controllers/Opportunity';
import { UploadsQueueActionTypes } from '@/store/reducers/uploads-queue/action-types';

import { OrdersActionTypes } from '../orders/action-types';

import { ActivitiesActionTypes } from './action-types';
import { initialState } from './constants';
import { ActivitiesReducerState as State } from './types';

export const activitiesReducer: Reducer<State> = (state = initialState, action): State => {
  switch (action.type) {
    case ActivitiesActionTypes.FETCH_ACTIVITIES.START: {
      const { appliedFilters } = action.sagaPayload;

      return {
        ...state,
        appliedFilters,
      };
    }

    case ActivitiesActionTypes.FETCH_ACTIVITIES.SUCCESS: {
      const { page: _page, ...appliedFilters } = action.sagaPayload
        .appliedFilters as AppliedActivityListFilters;

      if (state.appliedFilters.type !== appliedFilters.type) {
        return state;
      }

      return {
        ...state,
        appliedFilters,
        list: [...(state.list || []), ...action.payload.itemList],
        total: action.payload.totalCount,
        starredPresent: action.payload.starredPresent || state.starredPresent,
      };
    }

    case ActivitiesActionTypes.REMOVE_ACTIVITIES:
      return { ...state, list: initialState.list, total: initialState.total };

    case ActivitiesActionTypes.FETCH_ACTIVITY_DETAILS_DATA.START:
      return { ...state, activityDetailsData: null };

    case ActivitiesActionTypes.FETCH_ACTIVITY_DETAILS_DATA.SUCCESS:
      return produce(state, (newState: State) => {
        newState.activityDetailsData = ActivityController.mapInstancesToDetailsData(
          action.payload.activityInstances,
        );

        if (newState.activityDetailsData && state.activityDetailsData) {
          newState.activityDetailsData.attachments = state.activityDetailsData.attachments;
        }
      });

    case ActivitiesActionTypes.FETCH_ORDERS_HISTORY_DATA.SUCCESS: {
      return produce(state, (newState: State) => {
        (newState.activityDetailsData as JobDetailsData).orderHistory = action.payload.orderHistory;
      });
    }

    case ActivitiesActionTypes.FETCH_AVAILABLE_INSTALL_DATES.SUCCESS: {
      return {
        ...state,
        availableInstallDates: action.payload,
      };
    }

    case ActivitiesActionTypes.FETCH_AVAILABLE_DELIVERY_DATES.SUCCESS: {
      return {
        ...state,
        availableDeliveryDates: action.payload,
      };
    }

    case ActivitiesActionTypes.FETCH_TASK_CARD_STATUSES.SUCCESS:
      return {
        ...state,
        taskStatuses: action.payload,
      };

    case OrdersActionTypes.FETCH_ORDER_ALERT_MESSAGE.SUCCESS:
      return produce(state, (newState: State) => {
        (newState.activityDetailsData as JobDetailsData).orderInfo.orderCard =
          action.payload.orderCard;
      });

    case ActivitiesActionTypes.SAVE_DETAILS_TARGET_ACTIVITY:
      return { ...state, detailsTargetActivity: action.payload };

    case ActivitiesActionTypes.UPDATE_DETAILS_TARGET_OPPORTUNITY: {
      const { activityDetailsData } = state;

      if (!activityDetailsData) return state;

      const currentProposal = state.selectedProposal;
      const opportunity: Opportunity = action.payload;
      const selectedProposal = opportunity.proposals.find(p => p.id === currentProposal?.id);

      return {
        ...state,
        activityDetailsData: { ...activityDetailsData, opportunity },
        selectedProposal: selectedProposal ?? null,
      };
    }

    case ActivitiesActionTypes.SAVE_CREATED_BUCKET_NAME:
      return produce(state, (newState: State) => {
        if (newState.activityDetailsData) {
          newState.activityDetailsData.bucketId = action.payload;
        }
      });

    case UploadsQueueActionTypes.FETCH_ATTACHMENTS_LIST.SUCCESS:
      return {
        ...state,
        activityDetailsData: {
          ...state.activityDetailsData,
          attachments: concat(state.activityDetailsData?.attachments, action.payload),
        } as ActivityDetailsData,
      };

    case UploadsQueueActionTypes.CLEAR_ATTACHMENTS_LIST:
      return {
        ...state,
        activityDetailsData: {
          ...state.activityDetailsData,
          attachments: [],
        } as ActivityDetailsData,
      };

    case UploadsQueueActionTypes.DELETE_ATTACHMENT.SUCCESS:
      return {
        ...state,
        activityDetailsData: {
          ...state.activityDetailsData,
          attachments: state.activityDetailsData?.attachments.filter(
            upload => upload.id !== action.payload.id,
          ),
        } as ActivityDetailsData,
      };

    case ActivitiesActionTypes.SELECT_PROPOSAL:
      return {
        ...state,
        selectedProposal: action.payload,
      };

    default:
      return state;
  }
};
